import React, {
  useEffect,
  useState,
  useContext,
  useRef,
} from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
// eslint-disable-next-line import/no-extraneous-dependencies
import Grid from '@mui/material/Grid';
// eslint-disable-next-line import/no-extraneous-dependencies
import Button from '@mui/material/Button';
// eslint-disable-next-line import/no-extraneous-dependencies
import SvgIcon from '@mui/material/SvgIcon';
import DownloadIcon from '@digital-hig/icon/lib/esm/icons/download';
// eslint-disable-next-line import/no-extraneous-dependencies
import TextLink from '@mui/material/Link';
import styled from 'styled-components';
import window from 'global';
import EnvContext from '../common/envContext';
import { pushEvent } from '../common/analyticsApi';
import {
  generateCuratedListInfo,
  generateTutorialInfo,
} from '../common/userUtils';
import {
  hasFetchedLearningRecords,
  getLearningRecords,
  isAuthChecked,
  getUserId,
} from '../store/userSlice';
import {
  getFilesSizes,
} from '../common/utilities';
import {
  ENVIRONMENT, iconMap,
} from '../common/constants';

const DownloadableResources = ({
  tutorialData, curatedData,
}) => {
  const linkRef = useRef([]);
  const [resourceFileSizes, setResourceFileSizes] = useState(null);
  const [tutorialInfo, setTutorialInfo] = useState(null);
  const [curatedInfo, setCuratedInfo] = useState(null);
  const envContext = useContext(EnvContext);
  const env = envContext[ENVIRONMENT];
  const authChecked = useSelector(isAuthChecked);
  const learningRecords = useSelector(getLearningRecords);
  const recordChecked = useSelector(hasFetchedLearningRecords);
  const uid = useSelector(getUserId);

  useEffect(() => {
    if (tutorialData.dataSets && tutorialData.dataSets.length) {
      getFilesSizes(tutorialData.dataSets).then((sizes) => {
        setResourceFileSizes(sizes);
      });
    }
  }, []);

  useEffect(() => {
    const tutorialInfoData = generateTutorialInfo({ tutorialData, learningRecords, env });
    setTutorialInfo(tutorialInfoData);

    if (curatedData) {
      const curatedInfoData = generateCuratedListInfo({ curatedData, learningRecords, env });
      setCuratedInfo(curatedInfoData);
    }
  }, [authChecked, recordChecked, uid]);

  const handleAnalytics = (resource) => {
    const event = {};
    event.eventData = {
      eventName: 'link_click',
      values: tutorialInfo.values,
    };
    event.link = {
      linkSection: 'Tutorial Download',
      linkTitle: resource,
    };
    event.tutorialInfo = tutorialInfo.tutorialInfo;
    if (curatedInfo) {
      event.curatedListInfo = curatedInfo.curatedListInfo;
    }
    pushEvent({ event });
  };

  const buildStr = (resource) => {
    let fileNameStr = 'Tutorial assets';
    const extension = resource.substring(resource.lastIndexOf('.') + 1, resource.length);
    const fileName = resource.substring(resource.lastIndexOf('/') + 1, resource.length);
    if (resourceFileSizes && resourceFileSizes[resource]) {
      fileNameStr = `${window.decodeURIComponent(fileName)} - (${resourceFileSizes[resource]})`;
    } else if (extension) {
      fileNameStr = `${window.decodeURIComponent(fileName)}`;
    }
    const Icon = iconMap[extension] ? iconMap[extension] : iconMap.generic;
    return (
      <>
        <SvgIcon size="medium"><Icon /></SvgIcon>
        {' '}
        <TextLink
          href={resource}
          ref={((element) => element && linkRef.current.push(element))}
          variant="body-copy-small"
          onClick={() => handleAnalytics(resource)}
        >
          {fileNameStr}
        </TextLink>
      </>
    );
  };

  const handleDownloadAll = () => {
    const allDownlowdableLinks = new Set();
    linkRef.current.forEach((link) => allDownlowdableLinks.add(link));
    allDownlowdableLinks.forEach((link) => {
      link.querySelector('a').click();
    });
  };

  return (
    <RootElement>
      {tutorialData.dataSets && tutorialData.dataSets.length !== 0
            && (
              <Grid container rowSpacing="2" columnSpacing="1" className="tutorial-resources">
                <Grid xs={12} className="tutorial-assets">
                  {tutorialData.dataSets.map((resource) => (
                    <div className="asset">
                      {buildStr(resource)}
                    </div>
                  ))}
                  {tutorialData.dataSets.length > 1 && (
                    <Grid xs="auto">
                      <Button
                        size="small"
                        startIcon={<SvgIcon fontSize="medium"><DownloadIcon /></SvgIcon>}
                        variant="outlined"
                        onClick={handleDownloadAll}
                      >
                        Download all tutorial resources
                      </Button>
                    </Grid>
                  )}
                </Grid>
              </Grid>
            )}
    </RootElement>
  );
};

const RootElement = styled.div`
  margin-bottom: 22px;

  .tutorial-resources {
    margin-top: 0px;

    .tutorial-assets {
      display: flex;
      flex-direction: column;
      gap: 10px;

      .asset {
        label {
          padding-left: 8px;
        }
      }

      button {
        font-size: 14px;
        padding: 6px 10px;
        margin-top: 10px;
      }
    }
  }

`;

DownloadableResources.propTypes = {
  tutorialData: PropTypes.shape({ dataSets: PropTypes.arrayOf() }),
  curatedData: PropTypes.shape({}),
};

DownloadableResources.defaultProps = {
  tutorialData: {},
  curatedData: null,
};

export default DownloadableResources;
